import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiJson } from "@/lib/api-client";

export type AwsMediaConnectOutput = {
  arn: string;
  name: string;
  protocol?: string;
  destination?: string;
  port?: number;
  streamId?: string;
  listenerAddress?: string;
  outputStatus?: string;
  peerIpAddress?: string;
  routerIntegrationState?: string;
  connectedRouterInputArn?: string;
  healthStatus?: AwsCloudWatchConnectionHealth;
  metrics: AwsCloudWatchMetricSnapshot[];
};

export type AwsCloudWatchConnectionHealth = {
  status: "CONNECTED" | "DISCONNECTED" | "NO_DATA";
  metricName: "SourceConnected" | "OutputConnected";
  value?: number;
  timestamp?: string;
};

export type AwsCloudWatchMetricSnapshot = {
  metricName: string;
  label: string;
  value?: number;
  timestamp?: string;
  unit?: string;
  stat: "Average" | "Maximum" | "Sum";
};

export type AwsCloudWatchMetricHistory = AwsCloudWatchMetricSnapshot & {
  points: Array<{
    timestamp: string;
    value: number;
  }>;
};

export type AwsMediaConnectFlow = {
  arn: string;
  name: string;
  status: string;
  availabilityZone?: string;
  source?: {
    arn?: string;
    name?: string;
    protocol?: string;
    ingestIp?: string;
    ingestPort?: number;
    peerIpAddress?: string;
    routerIntegrationState?: string;
    connectedRouterOutputArn?: string;
    healthStatus?: AwsCloudWatchConnectionHealth;
    metrics: AwsCloudWatchMetricSnapshot[];
    whitelistCidr: "0.0.0.0/0";
  };
  outputs: AwsMediaConnectOutput[];
};

export type AwsMediaLiveInputDevice = {
  id: string;
  arn?: string;
  name?: string;
  type?: string;
  state?: string;
  connectionState?: string;
};

export type AwsMediaLiveInput = {
  id: string;
  arn?: string;
  name?: string;
  type?: string;
  state?: string;
  attachedChannels: string[];
  inputDevices: Array<{ id?: string }>;
};

export type AwsMediaLiveChannel = {
  id: string;
  arn?: string;
  name?: string;
  state?: string;
  channelClass?: string;
  pipelinesRunningCount?: number;
  inputAttachments: Array<{
    inputId?: string;
    name?: string;
  }>;
  destinations: Array<{
    id?: string;
    urls: string[];
  }>;
  egressEndpoints: string[];
};

export type AwsMediaThumbnail = {
  contentType?: string;
  dataUrl: string;
  lastModified?: string;
  timestamp?: string;
  pipelineId?: string;
  thumbnailType?: string;
};

export type AwsMediaSummary = {
  region: "eu-central-1";
  mediaConnect: {
    flows: AwsMediaConnectFlow[];
  };
  mediaLive: {
    channels: AwsMediaLiveChannel[];
    inputs: AwsMediaLiveInput[];
    inputDevices: AwsMediaLiveInputDevice[];
  };
};

export type CreateMediaConnectFlowInput = {
  name: string;
  sourceName: string;
  sourceProtocol: "rtp" | "srt-listener" | "zixi-push" | "rist";
  sourcePort: number;
  availabilityZone?: string;
};

export type AddMediaConnectOutputInput = {
  flowArn: string;
  output: {
    name: string;
    protocol:
      | "srt-caller"
      | "srt-listener"
      | "zixi-push"
      | "zixi-pull"
      | "rist"
      | "rtp";
    destination?: string;
    port?: number;
    streamId?: string;
  };
};

export type UpdateMediaConnectOutputInput = {
  flowArn: string;
  outputArn: string;
  output: Omit<AddMediaConnectOutputInput["output"], "name">;
};

export type AudioMappingInput = {
  selectorName: string;
  selectorType: "track" | "pid" | "language";
  track?: number;
  pid?: number;
  languageCode?: string;
  outputName: string;
  bitrate: number;
  sampleRate: number;
};

export type CreateMediaLiveChannelInput = {
  name: string;
  inputId?: string;
  inputDeviceId?: string;
  inputName?: string;
  outputMode: "manual" | "mediaconnect_flow";
  manualDestinationUrl?: string;
  mediaConnectFlowArn?: string;
  channelClass: "SINGLE_PIPELINE" | "STANDARD";
  videoBitrate: number;
  width: number;
  height: number;
  framerateNumerator: number;
  framerateDenominator: number;
  audioMappings: AudioMappingInput[];
};

export type UpdateMediaLiveChannelConfigInput = {
  channelId: string;
  config: {
    name?: string;
    outputMode: "manual" | "mediaconnect_flow";
    manualDestinationUrl?: string;
    mediaConnectFlowArn?: string;
    videoBitrate: number;
    width: number;
    height: number;
    framerateNumerator: number;
    framerateDenominator: number;
    audioMappings: AudioMappingInput[];
  };
};

export type UpdateMediaLiveChannelConfigResult = {
  channel: AwsMediaLiveChannel | null;
  queued: boolean;
  operationId?: string;
  queuedAt?: string;
  pendingCount?: number;
};

export type MediaLiveConfigUpdateQueueStatus = {
  pendingCount: number;
  activeOperationId?: string;
  lastQueuedAt?: string;
  lastAppliedAt?: string;
  lastFailedAt?: string;
  lastError?: string;
};

export type MediaConnectMetricsResult = {
  region: "eu-central-1";
  resourceArn: string;
  resourceType: "source" | "output";
  periodMinutes: number;
  metrics: AwsCloudWatchMetricHistory[];
};

const SUMMARY_KEY = ["aws-media", "summary"] as const;

function encodedArn(arn: string) {
  return encodeURIComponent(arn);
}

export function useAwsMediaSummary() {
  return useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: () => apiJson<AwsMediaSummary>("/api/aws-media/summary"),
    refetchInterval: 15000,
  });
}

export function useMediaConnectMetrics(
  resourceArn?: string,
  resourceType?: "source" | "output",
  periodMinutes = 60,
) {
  return useQuery({
    queryKey: [
      "aws-media",
      "mediaconnect-metrics",
      resourceArn,
      resourceType,
      periodMinutes,
    ],
    queryFn: () =>
      apiJson<MediaConnectMetricsResult>(
        `/api/aws-media/mediaconnect/metrics?resourceArn=${encodedArn(
          resourceArn ?? "",
        )}&resourceType=${resourceType}&periodMinutes=${periodMinutes}`,
      ),
    enabled: Boolean(resourceArn && resourceType),
    refetchInterval: 15000,
  });
}

export function useCreateMediaConnectFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMediaConnectFlowInput) =>
      apiJson<{ flow: AwsMediaConnectFlow }>(
        "/api/aws-media/mediaconnect/flows",
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaConnect flow created");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useMediaConnectFlowAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      flowArn,
      action,
    }: {
      flowArn: string;
      action: "start" | "stop";
    }) =>
      apiJson<{ flow: AwsMediaConnectFlow }>(
        `/api/aws-media/mediaconnect/flows/${encodedArn(flowArn)}/${action}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useAddMediaConnectOutput() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ flowArn, output }: AddMediaConnectOutputInput) =>
      apiJson<{ flow: AwsMediaConnectFlow }>(
        `/api/aws-media/mediaconnect/flows/${encodedArn(flowArn)}/outputs`,
        {
          method: "POST",
          body: JSON.stringify(output),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaConnect output added");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useUpdateMediaConnectOutput() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      flowArn,
      outputArn,
      output,
    }: UpdateMediaConnectOutputInput) =>
      apiJson<{ flow: AwsMediaConnectFlow; output: AwsMediaConnectOutput }>(
        `/api/aws-media/mediaconnect/flows/${encodedArn(flowArn)}/outputs/${encodedArn(outputArn)}`,
        {
          method: "PUT",
          body: JSON.stringify(output),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaConnect output updated");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useRemoveMediaConnectOutput() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      flowArn,
      outputArn,
    }: {
      flowArn: string;
      outputArn: string;
    }) =>
      apiJson(
        `/api/aws-media/mediaconnect/flows/${encodedArn(flowArn)}/outputs/${encodedArn(outputArn)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaConnect output removed");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useCreateMediaLiveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMediaLiveChannelInput) =>
      apiJson<{ channel: AwsMediaLiveChannel }>(
        "/api/aws-media/medialive/channels",
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaLive channel created");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useMediaLiveChannelConfig(channelId?: string) {
  return useQuery({
    queryKey: ["aws-media", "medialive-channel-config", channelId],
    queryFn: () =>
      apiJson<{ channel: Record<string, unknown> }>(
        `/api/aws-media/medialive/channels/${channelId}/config`,
      ),
    enabled: Boolean(channelId),
  });
}

export function useMediaLiveConfigUpdateQueue(
  channelId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["aws-media", "medialive-config-update-queue", channelId],
    queryFn: () =>
      apiJson<{
        channelId: string;
        queue: MediaLiveConfigUpdateQueueStatus;
      }>(`/api/aws-media/medialive/channels/${channelId}/config-update-queue`),
    enabled: enabled && Boolean(channelId),
    refetchInterval: 5000,
  });
}

export function useMediaLiveInputDeviceThumbnail(
  inputDeviceId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["aws-media", "medialive-input-device-thumbnail", inputDeviceId],
    queryFn: () =>
      apiJson<AwsMediaThumbnail>(
        `/api/aws-media/medialive/input-devices/${inputDeviceId}/thumbnail`,
      ),
    enabled: enabled && Boolean(inputDeviceId),
    refetchInterval: 60000,
    retry: 1,
  });
}

export function useMediaLiveChannelThumbnails(
  channelId?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["aws-media", "medialive-channel-thumbnails", channelId],
    queryFn: () =>
      apiJson<{ thumbnails: AwsMediaThumbnail[] }>(
        `/api/aws-media/medialive/channels/${channelId}/thumbnails`,
      ),
    enabled: enabled && Boolean(channelId),
    refetchInterval: 60000,
    retry: 1,
  });
}

export function useUpdateMediaLiveChannelConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, config }: UpdateMediaLiveChannelConfigInput) =>
      apiJson<UpdateMediaLiveChannelConfigResult>(
        `/api/aws-media/medialive/channels/${channelId}/config`,
        {
          method: "PUT",
          body: JSON.stringify(config),
        },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      queryClient.invalidateQueries({
        queryKey: [
          "aws-media",
          "medialive-channel-config",
          variables.channelId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "aws-media",
          "medialive-config-update-queue",
          variables.channelId,
        ],
      });
      toast.success("MediaLive channel config update queued");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useMediaLiveChannelAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelId,
      action,
    }: {
      channelId: string;
      action: "start" | "stop";
    }) =>
      apiJson<{ channel: AwsMediaLiveChannel }>(
        `/api/aws-media/medialive/channels/${channelId}/${action}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteMediaLiveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) =>
      apiJson(`/api/aws-media/medialive/channels/${channelId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaLive channel deleted");
    },
    onError: (error) => toast.error(error.message),
  });
}

export function useDeleteMediaConnectFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (flowArn: string) =>
      apiJson(`/api/aws-media/mediaconnect/flows/${encodedArn(flowArn)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
      toast.success("MediaConnect flow deleted");
    },
    onError: (error) => toast.error(error.message),
  });
}
